"use client";

import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "@/components/common/Dialog";
import { getAgreementData, trackUserEvent } from "@/app/api/client-only";
import { AgreementData } from "@/types/journal";
import { LocaleData } from "@/types/i18n";
import { useLocale } from "@/context/LocaleContext";
import { formatDate } from "@/utils/format-date";
import "./results-table.scss";

interface AgreementModalProps {
  open: boolean;
  onClose: () => void;
  journalName: string;
  journalUrl?: string;
  journalId?: string;
  slug: string;
  agreementId: string;
  localizedData: LocaleData;
  linkTarget: string;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

export function AgreementModal({
  open,
  onClose,
  journalName,
  journalUrl,
  journalId,
  slug,
  agreementId,
  localizedData,
  linkTarget,
  returnFocusRef,
}: AgreementModalProps) {
  const [data, setData] = useState<AgreementData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { locale } = useLocale();

  useEffect(() => {
    setData(null);
    setError(false);
  }, [agreementId]);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    setError(false);
    getAgreementData(slug, agreementId).then((result) => {
      if (result) {
        setData(result);
      } else {
        setError(true);
      }
      setLoading(false);
    });
  }, [open, slug, agreementId, data]);

  const hasTextContent = (html: string) =>
    html.replace(/<[^>]*>/g, "").trim().length > 0;
  const description = data?.description
    ? ([data.description[locale], data.description["en"]].find(
        (html) => html && hasTextContent(html),
      ) ?? undefined)
    : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      title={
        journalName.length > 70 ? `${journalName.slice(0, 70)}…` : journalName
      }
      titleTooltip={journalName.length > 70 ? journalName : undefined}
      initialFocusRef={contentRef}
      returnFocusRef={returnFocusRef}
      className="agreementModalDialog"
    >
      <div ref={contentRef} className="agreementModalContent" tabIndex={0}>
        {journalUrl && (
          <div className="agreementModalJournalUrl">
            <a
              href={journalUrl}
              target={linkTarget}
              rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
            >
              {localizedData.agreement_modal_open_journal ?? "Go to Journal"}
            </a>
          </div>
        )}
        {loading && (
          <p>{localizedData.agreement_modal_loading ?? "Loading..."}</p>
        )}
        {error && (
          <p>
            {localizedData.agreement_modal_error ??
              "Failed to load agreement details."}
          </p>
        )}
        {data && (
          <>
            <dl className="agreementModalFields">
              {data.end_date && (
                <>
                  <dt>
                    {localizedData.agreement_modal_end_date ??
                      "Agreement expiration date"}
                  </dt>
                  <dd>{formatDate(data.end_date, locale) ?? data.end_date}</dd>
                </>
              )}
              {data.payment_option && (
                <>
                  <dt>
                    {localizedData.agreement_modal_payment_option ??
                      "Payment option"}
                  </dt>
                  <dd>{data.payment_option}</dd>
                </>
              )}
              {description && (
                <>
                  <dt>
                    {localizedData.agreement_modal_description ??
                      "Agreement description"}
                  </dt>
                  <dd
                    className="agreementModalDescription"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </>
              )}
            </dl>
            {data.payment_url && (
              <div className="agreementModalPaymentUrl">
                <a
                  href={data.payment_url}
                  target={linkTarget}
                  rel={
                    linkTarget === "_blank" ? "noopener noreferrer" : undefined
                  }
                  // Links straight to the payment page rather than through
                  // /links, which falls back to a generic page when the
                  // search index has a stale journal id.
                  onClick={() => {
                    trackUserEvent(slug, "p", "agreement_description", {
                      journal: journalId,
                      agreement: agreementId,
                    }).catch(() => {});
                  }}
                >
                  {localizedData.agreement_modal_payment_url ??
                    data.payment_url}
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
